import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';
const G = BRAND.colors.primary;

// Color presets to make picking quick. Distributors who want exact brand match
// can still use the native color input next to these.
const COLOR_PRESETS = [
  '#10B981', // emerald
  '#3B82F6', // blue
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#F59E0B', // amber
  '#EF4444', // red
  '#0EA5E9', // sky
  '#0F172A', // slate (dark)
];

export default function StorefrontPage({ dist, getToken, showToast, onSave }) {
  // Editable settings
  const [company,    setCompany]    = useState(dist?.company || '');
  const [tagline,    setTagline]    = useState(dist?.tagline || 'Your branded merchandise partner');
  const [heroSubtext, setHeroSubtext] = useState(dist?.hero_subtext || 'Browse our catalog and request a quote — we\'ll respond within 24 hours');
  // Hero customization (June 2026 "Direction C" redesign). All three optional —
  // empty/null falls back to defaults in PublicStorefront. Together they let
  // each distributor write their own hero copy without code changes.
  const [heroEyebrow,        setHeroEyebrow]        = useState(dist?.hero_eyebrow || '');
  const [heroHeadline,       setHeroHeadline]       = useState(dist?.hero_headline || '');
  const [heroHeadlineAccent, setHeroHeadlineAccent] = useState(dist?.hero_headline_accent || '');
  const [subdomain,  setSubdomain]  = useState(dist?.subdomain || '');
  const [phone,      setPhone]      = useState(dist?.contact_phone || '');
  const [email,      setEmail]      = useState(dist?.contact_email || '');
  const [brandColor, setBrandColor] = useState(dist?.brand_color || G);
  const [logoData,   setLogoData]   = useState(dist?.logo_data || null);  // base64 incl. data: prefix
  const [logoMime,   setLogoMime]   = useState(dist?.logo_mime || null);
  const [featured,   setFeatured]   = useState(Array.isArray(dist?.featured_products) ? dist.featured_products : []);

  const [saving, setSaving]   = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const fileRef = useRef(null);

  // Resolve featured product IDs to full product records for display/preview.
  // Re-fetched whenever the list of IDs changes.
  const [featuredProducts, setFeaturedProducts] = useState([]);
  useEffect(() => {
    (async () => {
      if (!featured || featured.length === 0) { setFeaturedProducts([]); return; }
      try {
        const token = await getToken();
        const data = await api.withToken(token).productsByIds(featured);
        const byId = Object.fromEntries((data.results || []).map(p => [p.id, p]));
        setFeaturedProducts(featured.map(id => byId[id]).filter(Boolean));
      } catch (e) {
        // Silently — preview will just show ID placeholders
      }
    })();
  }, [featured, getToken]);

  // Handle logo upload: read as data URL, validate size, store in state.
  // Saved to DB on next "Save & Publish" — not auto-uploaded.
  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|svg\+xml|webp|gif)$/i.test(file.type)) {
      showToast('Please use a PNG, JPG, SVG, WebP or GIF image', 'error');
      return;
    }
    if (file.size > 2_000_000) {
      showToast('Logo file too large — please use an image under 2MB', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLogoData(reader.result);  // data:image/...;base64,...
      setLogoMime(file.type);
    };
    reader.onerror = () => showToast('Could not read logo file', 'error');
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoData(null);
    setLogoMime(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      const updated = await api.withToken(token).updateMe({
        company, tagline, subdomain,
        hero_subtext:         heroSubtext,
        hero_eyebrow:         heroEyebrow,
        hero_headline:        heroHeadline,
        hero_headline_accent: heroHeadlineAccent,
        contact_phone: phone, contact_email: email,
        brand_color: brandColor,
        logo_data: logoData,
        logo_mime: logoMime,
        featured_products: featured,
      });
      onSave?.(updated);
      showToast('Storefront saved');
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const removeFeatured = (id) => {
    setFeatured(prev => prev.filter(x => x !== id));
  };

  const addFeatured = (id) => {
    setFeatured(prev => prev.includes(id) ? prev : [...prev, id].slice(0, 6));
  };

  // The storefront URL we surface to the distributor. During beta we use a
  // path-based route on the Railway deploy. When the real domain is live,
  // BRAND.domain takes over — no code change needed elsewhere.
  const storefrontUrl = subdomain
    ? `${window.location.origin}/store/${subdomain}`
    : `${window.location.origin}/store/your-slug`;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '420px 1fr', gap: 24 }}>
        {/* ── Left: settings panel ─────────────────────────────────────────── */}
        <div>
          <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#0F172A', marginBottom: 4 }}>Storefront Settings</div>
            <div style={{ fontSize: 13, color: '#64748B', marginBottom: 20 }}>Your white-label client-facing website.</div>

            <Field label="Company name (shown on site)">
              <input value={company} onChange={e => setCompany(e.target.value)}
                placeholder="Seattle Custom Printing" style={inputStyle} />
            </Field>

            <Field label="Tagline">
              <input value={tagline} onChange={e => setTagline(e.target.value)}
                placeholder="Your branded merchandise partner" style={inputStyle} />
            </Field>

            {/* ── Hero customization (optional) ─────────────────────────────── */}
            <div style={{
              marginTop: 18, marginBottom: 14, paddingTop: 16,
              borderTop: '1px solid #E2E8F0',
            }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#0F172A', marginBottom: 2 }}>
                Hero headline (optional)
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 12 }}>
                Customize the big text at the top of your storefront. Leave blank to use the tagline.
              </div>

              <Field label="Eyebrow label">
                <input value={heroEyebrow} onChange={e => setHeroEyebrow(e.target.value)}
                  placeholder="e.g. Curated catalog · Established 1998 · Wholesale promo"
                  style={inputStyle} />
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
                  Small label above the headline in your brand color. Optional — leave blank to hide.
                </div>
              </Field>

              <Field label="Headline (main text)">
                <input value={heroHeadline} onChange={e => setHeroHeadline(e.target.value)}
                  placeholder="e.g. Branded goods,"
                  style={inputStyle} />
              </Field>

              <Field label="Headline (accent phrase)">
                <input value={heroHeadlineAccent} onChange={e => setHeroHeadlineAccent(e.target.value)}
                  placeholder="e.g. sourced right."
                  style={inputStyle} />
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
                  Appears at the end of the headline in your brand color for emphasis.
                </div>
              </Field>
            </div>

            <Field label="Subtext (under the tagline)">
              <textarea value={heroSubtext} onChange={e => setHeroSubtext(e.target.value)}
                placeholder="Browse our catalog and request a quote — we'll respond within 24 hours"
                rows={2}
                style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical', minHeight: 56 }} />
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
                Customize the welcome message clients see on your storefront homepage.
              </div>
            </Field>

            <Field label="Contact email (shown to clients)">
              <input value={email} onChange={e => setEmail(e.target.value)}
                placeholder="hello@yourcompany.com" style={inputStyle} />
            </Field>

            <Field label="Contact phone">
              <input value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="206-555-0100" style={inputStyle} />
            </Field>

            <Field label="Storefront URL slug" hint={`Your storefront: ${storefrontUrl}`}>
              <input
                value={subdomain}
                onChange={e => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="yourcompany"
                style={inputStyle}
              />
            </Field>

            {/* ── Logo upload ──────────────────────────────────────────────── */}
            <div style={{ marginBottom: 16 }}>
              <label style={fieldLabelStyle}>Company logo</label>
              {logoData ? (
                <div style={{
                  border: '1px solid #E2E8F0', borderRadius: 8, padding: 12,
                  display: 'flex', alignItems: 'center', gap: 12, background: '#F8FAFC',
                }}>
                  <div style={{
                    width: 60, height: 60, background: '#fff', borderRadius: 6, border: '1px solid #E2E8F0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                  }}>
                    <img src={logoData} alt="logo" style={{ maxWidth: 56, maxHeight: 56, objectFit: 'contain' }} />
                  </div>
                  <div style={{ flex: 1, fontSize: 12, color: '#475569' }}>Logo uploaded</div>
                  <button onClick={removeLogo} style={{
                    background: 'none', border: 'none', color: '#dc2626', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                  }}>Remove</button>
                </div>
              ) : (
                <label style={{
                  display: 'block', border: '1px dashed #cbd5e1', borderRadius: 8,
                  padding: '18px 16px', textAlign: 'center', cursor: 'pointer',
                  fontSize: 12, color: '#64748B',
                }}>
                  <input ref={fileRef} type="file" accept="image/*" onChange={onLogoFile} style={{ display: 'none' }} />
                  📁 Click to upload (PNG, JPG, SVG · 2MB max)
                </label>
              )}
            </div>

            {/* ── Brand color ──────────────────────────────────────────────── */}
            <div style={{ marginBottom: 16 }}>
              <label style={fieldLabelStyle}>Primary brand color</label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                {COLOR_PRESETS.map(c => (
                  <button
                    key={c}
                    onClick={() => setBrandColor(c)}
                    style={{
                      width: 28, height: 28, borderRadius: 6, background: c,
                      border: brandColor === c ? '2px solid #0F172A' : '1px solid #E2E8F0',
                      cursor: 'pointer', padding: 0,
                    }}
                    title={c}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="color"
                  value={brandColor}
                  onChange={e => setBrandColor(e.target.value)}
                  style={{ width: 40, height: 32, border: '1px solid #E2E8F0', borderRadius: 6, cursor: 'pointer', padding: 2 }}
                />
                <input
                  value={brandColor}
                  onChange={e => setBrandColor(e.target.value)}
                  placeholder="#10B981"
                  style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12, width: 110, flex: 'none' }}
                />
                <div style={{ fontSize: 11, color: '#94A3B8' }}>Used for buttons & accents</div>
              </div>
            </div>

            {/* ── Featured products ────────────────────────────────────────── */}
            <div style={{ marginBottom: 18 }}>
              <label style={fieldLabelStyle}>Featured products (up to 6)</label>
              <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 8 }}>
                These appear on your storefront homepage. Pick a mix that represents your range.
              </div>
              {featuredProducts.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 8 }}>
                  {featuredProducts.map(p => (
                    <div key={p.id} style={{
                      display: 'flex', gap: 8, alignItems: 'center',
                      border: '1px solid #E2E8F0', borderRadius: 6, padding: 6, background: '#F8FAFC',
                    }}>
                      <div style={{ width: 32, height: 32, borderRadius: 4, background: '#fff', border: '1px solid #E2E8F0', overflow: 'hidden', flexShrink: 0 }}>
                        {p.images?.[0]?.url && <img src={p.images[0].url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                        <div style={{ fontSize: 10, color: '#94A3B8' }}>{p.category}</div>
                      </div>
                      <button onClick={() => removeFeatured(p.id)} style={{
                        background: 'none', border: 'none', color: '#dc2626', fontSize: 14, cursor: 'pointer', padding: 2, fontFamily: 'inherit',
                      }} title="Remove">×</button>
                    </div>
                  ))}
                </div>
              )}
              {featured.length < 6 && (
                <button onClick={() => setShowPicker(true)} style={{
                  width: '100%', padding: '10px 12px', background: '#fff',
                  border: '1px dashed #cbd5e1', borderRadius: 6, color: '#475569',
                  fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  + Pick a product to feature ({featured.length}/6)
                </button>
              )}
            </div>

            <button onClick={save} disabled={saving} style={{
              background: brandColor, border: 'none', borderRadius: 8, padding: '10px 20px',
              color: '#fff', fontSize: 13, fontWeight: 500,
              cursor: saving ? 'wait' : 'pointer', fontFamily: 'inherit', width: '100%',
            }}>
              {saving ? 'Saving…' : 'Save & Publish'}
            </button>
          </div>
        </div>

        {/* ── Right: live preview ─────────────────────────────────────────── */}
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#0F172A', marginBottom: 10 }}>Live Preview</div>
          <Preview
            company={company}
            tagline={tagline}
            heroSubtext={heroSubtext}
            phone={phone}
            brandColor={brandColor}
            logoData={logoData}
            featuredProducts={featuredProducts}
            storefrontUrl={storefrontUrl}
          />
        </div>
      </div>

      {/* Featured-product picker modal */}
      {showPicker && (
        <ProductPickerModal
          getToken={getToken}
          showToast={showToast}
          excludeIds={featured}
          onClose={() => setShowPicker(false)}
          onPick={(id) => { addFeatured(id); setShowPicker(false); }}
        />
      )}
    </div>
  );
}

// ─── Live preview ────────────────────────────────────────────────────────────
function Preview({ company, tagline, heroSubtext, phone, brandColor, logoData, featuredProducts, storefrontUrl }) {
  const initial = (company || 'Y')[0].toUpperCase();
  return (
    <div style={{ border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
      {/* Browser chrome */}
      <div style={{ background: '#0F172A', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ display: 'flex', gap: 5 }}>
          {[1, 2, 3].map(i => <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }} />)}
        </div>
        <div style={{ flex: 1, background: 'rgba(255,255,255,0.08)', borderRadius: 5, padding: '3px 12px', fontSize: 11, color: '#94A3B8', textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
          {storefrontUrl}
        </div>
      </div>
      <div style={{ background: '#fff' }}>
        {/* Top nav */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #F1F5F9' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {logoData ? (
              <img src={logoData} alt="logo" style={{ height: 32, maxWidth: 140, objectFit: 'contain' }} />
            ) : (
              <div style={{ width: 32, height: 32, borderRadius: 6, background: brandColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 700 }}>{initial}</div>
            )}
            <span style={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>{company || 'Your Company'}</span>
          </div>
          <span style={{ fontSize: 12, color: brandColor }}>{phone || 'Your phone'}</span>
        </div>

        {/* Hero */}
        <div style={{ padding: '36px 20px', background: `linear-gradient(135deg, ${brandColor}10, ${brandColor}05)`, textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', marginBottom: 8, letterSpacing: '-0.3px' }}>{tagline}</div>
          <div style={{ fontSize: 12, color: '#64748B', marginBottom: 20 }}>{heroSubtext || 'Browse our catalog and request a quote — we\'ll respond within 24 hours'}</div>
          <div style={{ maxWidth: 360, margin: '0 auto', display: 'flex', gap: 8 }}>
            <input placeholder="Search products..." style={{ flex: 1, border: '1px solid #E2E8F0', borderRadius: 7, padding: '9px 12px', fontSize: 12, fontFamily: 'inherit', outline: 'none' }} />
            <button style={{ background: brandColor, border: 'none', borderRadius: 7, padding: '9px 18px', color: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Search</button>
          </div>
        </div>

        {/* Featured grid */}
        {featuredProducts.length > 0 && (
          <div style={{ padding: '20px' }}>
            <div style={{ fontSize: 12, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Featured</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {featuredProducts.slice(0, 6).map(p => (
                <div key={p.id} style={{ border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ height: 70, background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {p.images?.[0]?.url
                      ? <img src={p.images[0].url} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      : <span style={{ fontSize: 24 }}>📦</span>}
                  </div>
                  <div style={{ padding: '6px 8px' }}>
                    <div style={{ fontSize: 10, fontWeight: 500, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                    <button style={{ width: '100%', marginTop: 4, background: '#F1F5F9', border: 'none', borderRadius: 4, padding: 4, fontSize: 9, color: '#475569', cursor: 'pointer', fontFamily: 'inherit' }}>Request Quote</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ padding: '12px 20px 18px', fontSize: 10, color: '#94A3B8', textAlign: 'center' }}>
          Pricing varies based on decoration, quantity, and shipping. Your distributor will confirm all charges before production.
        </div>
      </div>
    </div>
  );
}

// ─── Product picker modal ────────────────────────────────────────────────────
function ProductPickerModal({ getToken, showToast, excludeIds, onClose, onPick }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const doSearch = async (q) => {
    if (!q || !q.trim()) return;
    setSearching(true);
    setHasSearched(true);
    try {
      const token = await getToken();
      const data = await api.withToken(token).search(q.trim(), { limit: 30 });
      setResults((data?.results || []).filter(p => !excludeIds.includes(p.id)));
    } catch (e) {
      showToast?.(e.message || 'Search failed', 'error');
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Pick a product to feature</div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 24, cursor: 'pointer', color: '#94a3b8', padding: 0 }}>×</button>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              autoFocus
              placeholder="Search the catalog (e.g. tote bag, water bottle)…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doSearch(query)}
              style={{ flex: 1, padding: '10px 14px', border: '1px solid #E2E8F0', borderRadius: 7, fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
            />
            <button onClick={() => doSearch(query)} disabled={searching || !query.trim()} style={{
              background: G, color: '#fff', border: 'none', borderRadius: 7,
              padding: '10px 18px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
              fontFamily: 'inherit', opacity: searching ? 0.7 : 1,
            }}>
              {searching ? 'Searching…' : 'Search'}
            </button>
          </div>
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {!hasSearched && (
              <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: 13, padding: 30 }}>
                Search the catalog above to find products to feature.
              </div>
            )}
            {hasSearched && !searching && results.length === 0 && (
              <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: 13, padding: 30 }}>
                No matching products found.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {results.map(p => (
                <button
                  key={p.id}
                  onClick={() => onPick(p.id)}
                  style={{
                    display: 'flex', gap: 10, alignItems: 'center',
                    border: '1px solid #E2E8F0', borderRadius: 6, padding: 8,
                    background: '#fff', cursor: 'pointer', textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                >
                  <div style={{ width: 44, height: 44, background: '#F8FAFC', borderRadius: 4, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {p.images?.[0]?.url
                      ? <img src={p.images[0].url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ fontSize: 18 }}>📦</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                    <div style={{ fontSize: 10, color: '#94A3B8' }}>{p.supplier_name} · {p.category}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tiny shared bits ────────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', border: '1px solid #E2E8F0', borderRadius: 8, padding: '9px 12px',
  fontSize: 13, fontFamily: 'inherit', outline: 'none', color: '#0F172A',
  boxSizing: 'border-box',
};
const fieldLabelStyle = {
  fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 6, display: 'block',
};

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={fieldLabelStyle}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  zIndex: 1000, padding: '40px 20px', overflowY: 'auto',
};
const modalStyle = {
  background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720,
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};
